<template>
  <div
    class="action-card language-card"
    :class="{ hovered: isHovered, 'mobile-web': isMobileWeb }"
    @mouseenter="$emit('hover', true)"
    @mouseleave="$emit('hover', false)"
  >
    <div class="language-options">
      <button
        v-for="locale in locales"
        :key="locale.code"
        class="language-option"
        :class="{ selected: locale.code === currentLocale }"
        :style="{ backgroundImage: getFlag(locale) ? `url(${getFlag(locale)})` : undefined }"
        @click="selectLocale(locale.code)"
      >
        <span class="language-name">{{ formatName(locale) }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Locale {
  code: string;
  name: string;
  flag: string;
}

const props = defineProps<{
  locales: Locale[];
  currentLocale: string;
  currentFlag: string;
  currentName: string;
  isHovered?: boolean;
  isMobileWeb?: boolean;
}>();

const emit = defineEmits<{
  select: [code: string];
  hover: [hovered: boolean];
}>();

const selectLocale = (code: string) => {
  emit('select', code);
};

const formatName = (locale: Locale): string => {
  if (locale.name === 'Français') return 'FR';
  if (locale.name === 'English') return 'EN';
  return locale.name.substring(0, 2).toUpperCase();
};

const getFlag = (locale: Locale): string => {
  if (locale.name === 'Français') return '/country/fr.png';
  if (locale.name === 'English') return '/country/gb.png';
  return '';
};

defineExpose({
  closeOptions: () => {
    // No-op, kept for parent compatibility
  },
});
</script>

<style scoped>
.action-card {
  background: var(--ion-card-background);
  border-radius: 12px;
  padding: 15px;
  cursor: default;
  box-shadow: var(--ion-box-shadow);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  width: 250px;
  z-index: 1;
}

.action-card:hover {
  border: 1px solid var(--ion-input-border-color);
}

.language-options {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
}

.language-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 2px solid var(--ion-input-border-color);
  background-color: transparent;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.language-option::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  transition: background 0.2s ease;
}

.language-option.selected::before {
  background: rgba(255, 255, 255, 0.4);
}

.language-option:hover {
  border-color: var(--ion-input-border-color-hover);
}

.language-option.selected {
  border-color: var(--ion-input-border-color-hover);
}

.language-option.selected .language-name {
  font-weight: 700;
}

.language-flag {
  font-size: 24px;
}

.language-name {
  font-size: 16px;
  font-weight: 600;
  color: black;
  position: relative;
  z-index: 1;
}

/* Mobile styles */
.action-card.mobile-web {
  width: 100%;
  height: auto;
  min-height: 60px;
}
</style>
